import React, { useState } from "react";
import {
  TextField,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import useResponsiveFlags from "../hooks/useResponsiveFlags";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import axios from "../api/axios";

export default function ChangePassword() {
  const { isMobile } = useResponsiveFlags();
  const [values, setValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmPassword: false,
  });
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [currentPassError, setCurrentPassError] = useState("");
  const [newPassError, setNewPassError] = useState("");
  const [confirmPassError, setConfirmPassError] = useState("");

  const resetErrors = () => {
    setError("");
    setCurrentPassError("");
    setNewPassError("");
    setConfirmPassError("");
  };
  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value });
    resetErrors();
  };
  const handleClickShowPassword = (field) => {
    setValues({ ...values, [field]: !values[field] });
  };

  const validatePassword = () => {
    let isValid = true;
    resetErrors();
    const { currentPassword, newPassword, confirmPassword } = values;
    if (!currentPassword) {
      setCurrentPassError("Required.");
      isValid = false;
    }
    if (!newPassword) {
      setNewPassError("Required.");
      isValid = false;
    }
    if (!confirmPassword) {
      setConfirmPassError("Required.");
      isValid = false;
    }

    if (!isValid) return false;

    const passwordCriteria = {
      length: values.newPassword.length >= 8,
      hasUpper: /[A-Z]/.test(values.newPassword),
      hasLower: /[a-z]/.test(values.newPassword),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(values.newPassword),
    };

    if (values.newPassword !== values.confirmPassword) {
      setConfirmPassError("Passwords do not match.");
      isValid = false;
    }
    const errors = [];
    if (!passwordCriteria.length) errors.push("at least 8 characters");
    if (!passwordCriteria.hasUpper) errors.push("an uppercase letter");
    if (!passwordCriteria.hasLower) errors.push("a lowercase letter");
    if (!passwordCriteria.hasSpecial) errors.push("a special character");
    if (errors.length > 0) {
      setNewPassError(`Password must include ${errors.join(", ")}.`);
      isValid = false;
    }
    return isValid;
  };

  const handleSubmit = async () => {
    setLoading(true);
    const isValid = validatePassword();
    if (!isValid) {
      setLoading(false);
      return;
    } else {
      resetErrors();
      axios
        .post("/api/change-password", {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
          newPassword_confirmation: values.confirmPassword,
        })
        .then((res) => {
          if (res.data.errorcode) {
            if (res.data.errorcode === "1") setCurrentPassError(res.data.error);
            if (res.data.errorcode === "2") setNewPassError(res.data.error);
          } else if (res.data.message) {
            setSuccess(res.data.message);
            handleCancel();
          } else {
            setError("Failed to change password.");
          }
          setLoading(false);
        })
        .catch((err) => {
          setError("Failed to change password.");
          setLoading(false);
        });
    }
  };
  const handleCancel = () => {
    resetErrors();
    setValues({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    });
    setTimeout(() => {
      setSuccess();
    }, 5000);
  };

  return (
    <>
      <span className="custom-title" gutterBottom>
        Change Password
      </span>

      <div style={{ maxWidth: isMobile ? "47rem" : "40rem", padding: isMobile ? "" : "20px" }}>
        {error && (
          <Typography
            color="error"
            sx={{ marginLeft: isMobile ? "" : "12.5rem" }}
          >
            {error}
          </Typography>
        )}
        {success && (
          <Typography
            color="success"
            sx={{ marginLeft: isMobile ? "" : "12.5rem" }}
          >
            {success}
          </Typography>
        )}

        <div className={`${isMobile ? "" : "d-flex"}`}>
          <p className="change-password-fields sub-step-link">
            Current Password
          </p>
          <TextField
            label="Current Password"
            type={values.showCurrentPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            borderRadius="8px"
            error={!!currentPassError}
            helperText={
              currentPassError && (
                <span style={{ marginLeft: "-14px" }}>{currentPassError}</span>
              )
            }
            value={values.currentPassword}
            onChange={handleChange("currentPassword")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      handleClickShowPassword("showCurrentPassword")
                    }
                  >
                    {values.showCurrentPassword ? (
                      <VisibilityOffOutlinedIcon />
                    ) : (
                      <VisibilityOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={`${isMobile ? "" : "d-flex"}`}>
          <p className="change-password-fields sub-step-link">New Password</p>
          <TextField
            label="New Password"
            type={values.showNewPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            borderRadius="8px"
            value={values.newPassword}
            error={!!newPassError || !!confirmPassError}
            onChange={handleChange("newPassword")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleClickShowPassword("showNewPassword")}
                  >
                    {values.showNewPassword ? (
                      <VisibilityOffOutlinedIcon />
                    ) : (
                      <VisibilityOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText={
              <>
                {newPassError && (
                  <span style={{ marginLeft: "-14px" }}>{newPassError}</span>
                )}
                <Typography
                  variant="body2"
                  className="review-text"
                  style={{
                    marginBottom: "0",
                  }}
                >
                  <span style={{ marginLeft: "-14px" }}>
                    Password must:
                    <ul style={{ paddingLeft: "1rem", marginBottom: "0" }}>
                      <li>Be at least 8 characters</li>
                      <li>Include both uppercase and lowercase letters</li>
                      <li>
                        Include at least one special character (e.g., ! @ # $)
                      </li>
                    </ul>
                  </span>
                </Typography>
              </>
            }
          />
        </div>

        <div className={`${isMobile ? "" : "d-flex"}`}>
          <p className="change-password-fields sub-step-link">
            Confirm New Password
          </p>
          <TextField
            label="Confirm New Password"
            type={values.showConfirmPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            borderRadius="8px"
            value={values.confirmPassword}
            error={!!confirmPassError}
            helperText={
              confirmPassError && (
                <span style={{ marginLeft: "-14px" }}>{confirmPassError}</span>
              )
            }
            onChange={handleChange("confirmPassword")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      handleClickShowPassword("showConfirmPassword")
                    }
                  >
                    {values.showConfirmPassword ? (
                      <VisibilityOffOutlinedIcon />
                    ) : (
                      <VisibilityOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        
        <div
          className={`${isMobile ? "" : "d-flex"}`}
          style={{ marginTop: "20px" }}
        >
          {!isMobile && <p style={{ width: "12.4rem" }}></p>}
          <button
            className="btn cancel-btn"
            style={{ ...(isMobile && { width: "100%", marginBottom: "1rem" }) }}
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="continue-btn"
            style={{ ...(isMobile && { width: "100%" }), padding: "10px 25px" }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Processing..." : "Change Password"}
          </button>
        </div>
      </div>
    </>
  );
}
